/**
 * RAGSense Public Chat Widget
 *
 * Embeddable chat widget for public websites
 */

import './styles.css';
import { scrollToMessageStart } from './scroll.js';
import { APIClient } from './api.js';
import { createTurnstileProvider } from './turnstile.js';
import { getOrCreateSessionId, getOrCreateVisitorId, getConversationId, setConversationId, setSessionId, clearConversation, getStoredTranscript, appendToStoredTranscript, clearStoredTranscript } from './storage.js';
import { getBrowserFingerprint, getUTMParams } from './fingerprint.js';
import { detectLanguage, makeTranslator } from './i18n.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const loaderScriptSrc = (typeof document !== 'undefined' && document.currentScript && document.currentScript.src) || null;
// Derived from whichever <script> tag loaded the widget bundle. The auto-init
// flow refines this when it sees the actual embed script (handles the case
// where the bundle is loaded via eval / dynamic injection rather than a top-
// level <script src=...> — notably in the unit tests).
let widgetStylesheetUrl = loaderScriptSrc ? new URL('ragsense-widget.css', loaderScriptSrc).toString() : null;

const MARKDOWN_SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style'],
  FORBID_ATTR: ['style']
};

class RagSenseWidget {
  constructor() {
    this.config = null;
    this.apiClient = null;
    this.sessionId = null;
    this.visitorId = null;
    this.conversationId = null;
    this.messages = [];
    this.isOpen = false;
    this.isLoading = false;
    this.browserFingerprint = null;
    this.utmParams = null;
    this.feedbackState = {}; // Track feedback per message
    this.elements = null;
    this.styleConfig = null;
    this.initPromise = null;
    this.initTelemetrySent = new Set();
    this.isUnavailable = false;
  }

  /**
   * Initialize the widget
   */
  async init(options) {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.initialize(options);
    return this.initPromise;
  }

  async initialize(options) {
    try {
      // Validate required options
      if (!options.configToken) {
        throw new Error('configToken is required');
      }
      if (!options.apiBaseUrl) {
        throw new Error('apiBaseUrl is required');
      }

      // Resolve language from options / browser *before* building UI copy.
      this.language = detectLanguage({ language: options.language });
      this.t = makeTranslator(this.language);

      // Store options
      this.options = {
        apiBaseUrl: options.apiBaseUrl,
        configToken: options.configToken,
        position: options.position || 'bottom-right',
        primaryColor: options.primaryColor || '#667eea',
        title: options.title || this.t('chatTitle'),
        logoPath: options.logoPath || null,
        language: this.language,
        telemetryEnabled: options.telemetryEnabled === true
      };
      this.styleConfig = this.buildWidgetStyle(options.widgetStyle || {});
      this.storageScope = this.options.configToken;

      // Mount a usable shell before remote configuration. A failed config
      // request must not make the support entry point disappear completely.
      this.ensureUI();
      this.applyWidgetStyle();
      this.showInitializingState();
      this.revealLauncher();
      this.reportInitTelemetry('init_script_loaded');

      // Fetch widget configuration
      this.config = await APIClient.getConfig(
        this.options.apiBaseUrl,
        this.options.configToken
      );

      // If the user hasn't explicitly passed a language in options, let the
      // server-side config override (e.g. tenant's default_language).
      if (!options.language && this.config) {
        const serverLang = this.config.default_language || this.config.language;
        if (serverLang) {
          this.language = detectLanguage({ language: serverLang });
          this.t = makeTranslator(this.language);
          this.options.language = this.language;
        }
      }

      this.styleConfig = this.buildWidgetStyle({
        ...this.styleConfig,
        ...(this.config.widget_style || {})
      });
      this.options = {
        ...this.options,
        position: this.styleConfig.position,
        primaryColor: this.styleConfig.primary_color,
        title: this.styleConfig.title,
        logoPath: this.styleConfig.logo_path
      };

      this.applyWidgetStyle();
      this.showReadyState();
      this.revealLauncher();
      this.reportInitTelemetry('init_ready');

      // Get or create session ID
      this.sessionId = getOrCreateSessionId(this.storageScope);
      this.visitorId = getOrCreateVisitorId(this.storageScope);

      // Get existing conversation ID
      this.conversationId = getConversationId(this.storageScope);

      // Restore the visible transcript across page navigations (display-only;
      // the server keeps the authoritative history via conversation_id).
      this.restoreStoredTranscript();

      // Get browser fingerprint
      this.browserFingerprint = getBrowserFingerprint();

      // Get UTM parameters
      this.utmParams = getUTMParams();

      // Turnstile is only active when the server advertises a site key.
      const turnstileProvider = this.config.turnstile_site_key
        ? createTurnstileProvider(this.config.turnstile_site_key)
        : null;

      // Initialize API client
      this.apiClient = new APIClient(
        this.options.apiBaseUrl,
        this.options.configToken,
        this.sessionId,
        this.visitorId,
        'public',
        turnstileProvider
      );

    } catch (error) {
      console.error('Failed to initialize RAGSense widget:', error);
      const category = this.initFailureCategory(error);
      this.reportInitTelemetry(category, error?.status || null);
      this.showUnavailableState();
      this.initPromise = null;
    }
  }

  reportInitTelemetry(event, reason = null) {
    if (
      !this.options?.telemetryEnabled ||
      !this.options.apiBaseUrl ||
      !this.options.configToken ||
      this.initTelemetrySent.has(event)
    ) return;
    this.initTelemetrySent.add(event);

    const externalReporter = window.__ragsenseWidgetTelemetry?.report;
    if (typeof externalReporter === 'function') {
      externalReporter(event, reason);
      return;
    }
    APIClient.reportInitEvent(
      this.options.apiBaseUrl,
      this.options.configToken,
      event,
      reason
    );
  }

  initFailureCategory(error) {
    if (error?.code === 'cloudflare_challenge') return 'init_cloudflare_challenge';
    if (error?.code === 'domain_rejected') return 'init_domain_rejected';
    return 'init_config_blocked';
  }

  ensureUI() {
    if (!this.elements?.host || !document.body.contains(this.elements.host)) {
      this.createUI();
    }
  }

  /**
   * Create the widget UI
   */
  createUI() {
    // The host is the only element in the light DOM — host-page CSS cannot
    // reach anything inside the shadow root we attach below.
    const host = document.createElement('div');
    host.className = 'ragsense-widget-host';
    host.style.cssText = this.getPositionStyles();

    const shadowRoot = host.attachShadow({ mode: 'open' });
    this.shadowRoot = shadowRoot;

    // Inject our stylesheet *into the shadow root* so it applies to widget
    // elements but does not affect (and is not affected by) the host page.
    if (widgetStylesheetUrl) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = widgetStylesheetUrl;
      link.addEventListener('error', () => {
        host.dataset.stylesheetState = 'failed';
        this.reportInitTelemetry('init_css_failed');
      }, { once: true });
      link.addEventListener('load', () => {
        host.dataset.stylesheetState = 'loaded';
      }, { once: true });
      shadowRoot.appendChild(link);
    }

    // The original .ragsense-widget-container becomes the inner root inside
    // the shadow tree. Existing CSS selectors continue to work unchanged.
    const container = document.createElement('div');
    container.className = 'ragsense-widget-container';
    container.hidden = true;

    // Create floating button
    const t = this.t || makeTranslator(this.language || 'en');
    const button = document.createElement('button');
    button.className = 'ragsense-chat-button';
    button.hidden = true;
    button.innerHTML = '<span class="ragsense-launcher-icon">💬</span><span class="ragsense-launcher-label"></span>';
    button.setAttribute('aria-label', t('openChat'));
    button.onclick = () => this.toggleChat();

    // Create chat window structure. We assemble the structural HTML once
    // (safe static markup) and then set all user-visible, translatable text
    // via textContent / setAttribute, so no untrusted data ever reaches
    // innerHTML.
    const chatWindow = document.createElement('div');
    chatWindow.className = 'ragsense-chat-window hidden';
    chatWindow.style.cssText = this.getChatWindowPositionStyles();
    chatWindow.setAttribute('lang', this.language || 'en');
    chatWindow.hidden = true;
    chatWindow.innerHTML = `
      <div class="ragsense-chat-header">
        <div class="ragsense-header-content">
          <img src="data:image/svg+xml;base64,${this.getLogoBase64()}" alt="" class="ragsense-logo" />
          <h3 class="ragsense-chat-title"></h3>
        </div>
        <div class="ragsense-header-actions">
          <button class="ragsense-new-chat-btn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 4v6h6"></path>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
            </svg>
          </button>
          <button class="ragsense-expand-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"></path>
            </svg>
          </button>
          <button class="ragsense-chat-close">×</button>
        </div>
      </div>
      <div class="ragsense-init-status" role="status">
        <p class="ragsense-init-status-text"></p>
        <button class="ragsense-init-retry" type="button"></button>
      </div>
      <div class="ragsense-messages"></div>
      <div class="ragsense-input-area">
        <input type="text" class="ragsense-input" />
        <button class="ragsense-send-btn"></button>
      </div>
      <div class="ragsense-disclaimer">
        <span class="ragsense-disclaimer-text"></span>
        <a class="ragsense-legal-link" target="_blank" rel="noopener noreferrer"></a>
      </div>
    `;

    // Populate translated text/attributes via the DOM API (XSS-safe).
    const qTitle = chatWindow.querySelector('.ragsense-chat-title');
    const qNewChat = chatWindow.querySelector('.ragsense-new-chat-btn');
    const qExpand = chatWindow.querySelector('.ragsense-expand-btn');
    const qClose = chatWindow.querySelector('.ragsense-chat-close');
    const qInput = chatWindow.querySelector('.ragsense-input');
    const qSend = chatWindow.querySelector('.ragsense-send-btn');

    qTitle.textContent = this.options.title;
    // New-chat button is an icon-only control; the text label lives in
    // aria-label/title (set in applyWidgetStyle) so screen readers and
    // tooltips still expose it.
    qNewChat.setAttribute('aria-label', t('newChatAria'));
    qNewChat.setAttribute('title', t('newChatAria'));
    qExpand.setAttribute('aria-label', t('expandAria'));
    qExpand.setAttribute('title', t('expand'));
    qClose.setAttribute('aria-label', t('closeChat'));
    qInput.setAttribute('placeholder', t('messagePlaceholder'));
    qInput.setAttribute('aria-label', t('messageAria'));
    qSend.textContent = t('send');

    // Add elements to container
    container.appendChild(button);
    container.appendChild(chatWindow);
    shadowRoot.appendChild(container);
    document.body.appendChild(host);

    // Store references
    this.elements = {
      host,
      container,
      button,
      chatWindow,
      launcherLabel: button.querySelector('.ragsense-launcher-label'),
      logo: chatWindow.querySelector('.ragsense-logo'),
      title: chatWindow.querySelector('.ragsense-chat-title'),
      initStatus: chatWindow.querySelector('.ragsense-init-status'),
      initStatusText: chatWindow.querySelector('.ragsense-init-status-text'),
      initRetryBtn: chatWindow.querySelector('.ragsense-init-retry'),
      messagesContainer: chatWindow.querySelector('.ragsense-messages'),
      inputArea: chatWindow.querySelector('.ragsense-input-area'),
      input: chatWindow.querySelector('.ragsense-input'),
      sendBtn: chatWindow.querySelector('.ragsense-send-btn'),
      closeBtn: chatWindow.querySelector('.ragsense-chat-close'),
      expandBtn: chatWindow.querySelector('.ragsense-expand-btn'),
      newChatBtn: chatWindow.querySelector('.ragsense-new-chat-btn'),
      disclaimerText: chatWindow.querySelector('.ragsense-disclaimer-text'),
      legalLink: chatWindow.querySelector('.ragsense-legal-link')
    };

    // Attach event listeners
    this.attachEventListeners();

    this.applyWidgetStyle();
    this.loadExpandedState();
  }

  showInitializingState() {
    if (!this.elements) return;
    this.isUnavailable = false;
    this.elements.initStatus.hidden = false;
    this.elements.initStatusText.textContent = this.t('connecting');
    this.elements.initRetryBtn.hidden = true;
    this.elements.messagesContainer.hidden = true;
    this.elements.inputArea.hidden = true;
  }

  showReadyState() {
    if (!this.elements) return;
    this.isUnavailable = false;
    this.elements.initStatus.hidden = true;
    this.elements.messagesContainer.hidden = false;
    this.elements.inputArea.hidden = false;
  }

  showUnavailableState() {
    if (!this.elements) return;
    this.isUnavailable = true;
    this.elements.initStatus.hidden = false;
    this.elements.initStatusText.textContent = this.t('initError');
    this.elements.initRetryBtn.textContent = this.t('retry');
    this.elements.initRetryBtn.hidden = false;
    this.elements.initRetryBtn.onclick = () => {
      if (this.initPromise) return;
      this.showInitializingState();
      void this.init(this.options);
    };
    this.elements.messagesContainer.hidden = true;
    this.elements.inputArea.hidden = true;
    this.revealLauncher();
  }

  revealLauncher() {
    if (!this.elements?.container || !this.elements?.button || !this.elements?.chatWindow) return;

    this.elements.container.hidden = false;
    this.elements.button.hidden = false;
    this.elements.chatWindow.hidden = true;
    this.elements.chatWindow.classList.add('hidden');
    this.elements.button.style.display = 'flex';
    this.isOpen = false;
  }

  buildWidgetStyle(style = {}) {
    const t = this.t || makeTranslator(this.language || 'en');
    return {
      title: style.title || this.options?.title || t('chatTitle'),
      launcher_label: style.launcher_label || style.title || this.options?.title || t('chatTitle'),
      primary_color: style.primary_color || this.options?.primaryColor || '#667eea',
      header_background: style.header_background || style.primary_color || this.options?.primaryColor || '#667eea',
      user_bubble_color: style.user_bubble_color || style.primary_color || this.options?.primaryColor || '#667eea',
      assistant_bubble_style: style.assistant_bubble_style || 'soft',
      logo_path: style.logo_path || this.options?.logoPath || null,
      position: style.position || this.options?.position || 'bottom-right',
      radius: Number.isFinite(Number(style.radius)) ? Number(style.radius) : 12,
      disclaimer_text: style.disclaimer_text || t('disclaimer'),
      legal_link_text: style.legal_link_text || t('legal'),
      legal_link_url: style.legal_link_url || null,
      input_placeholder: style.input_placeholder || t('messagePlaceholder'),
      send_label: style.send_label || t('send'),
      welcome_message: style.welcome_message || null,
      new_chat_label: style.new_chat_label || t('newChatAria'),
      custom_css_variables: style.custom_css_variables || {}
    };
  }

  applyWidgetStyle() {
    if (!this.elements?.container || !this.styleConfig) return;

    const style = this.styleConfig;
    const variables = {
      '--ragsense-primary-color': style.primary_color,
      '--ragsense-header-background': style.header_background,
      '--ragsense-user-bubble-color': style.user_bubble_color,
      '--ragsense-radius': `${style.radius}px`,
      ...(style.custom_css_variables || {})
    };

    Object.entries(variables).forEach(([key, value]) => {
      if (key.startsWith('--') && value) {
        this.elements.container.style.setProperty(key, value);
      }
    });

    this.applyPositionStyles();
    this.elements.chatWindow.dataset.assistantBubbleStyle = style.assistant_bubble_style;
    this.elements.title.textContent = style.title;
    this.elements.launcherLabel.textContent = style.launcher_label;
    this.elements.button.setAttribute('aria-label', style.launcher_label);
    this.elements.button.setAttribute('title', style.launcher_label);
    this.elements.logo.src = style.logo_path || `data:image/svg+xml;base64,${this.getLogoBase64()}`;
    this.elements.disclaimerText.textContent = style.disclaimer_text;
    this.elements.input.setAttribute('placeholder', style.input_placeholder);
    this.elements.sendBtn.textContent = style.send_label;
    this.elements.newChatBtn.setAttribute('aria-label', style.new_chat_label);
    this.elements.newChatBtn.setAttribute('title', style.new_chat_label);
    this.ensureWelcomeMessage();

    if (style.legal_link_url) {
      this.elements.legalLink.textContent = style.legal_link_text;
      this.elements.legalLink.href = style.legal_link_url;
      this.elements.legalLink.classList.remove('hidden');
    } else {
      this.elements.legalLink.textContent = '';
      this.elements.legalLink.removeAttribute('href');
      this.elements.legalLink.classList.add('hidden');
    }
  }

  applyPositionStyles() {
    const position = this.styleConfig.position;
    const host = this.elements.host.style;
    const chatWindow = this.elements.chatWindow.style;

    host.top = '';
    host.right = '';
    host.bottom = '';
    host.left = '';
    chatWindow.top = '';
    chatWindow.right = '';
    chatWindow.bottom = '';
    chatWindow.left = '';

    if (position === 'bottom-left') {
      host.bottom = '20px';
      host.left = '20px';
      chatWindow.bottom = '90px';
      chatWindow.left = '20px';
    } else if (position === 'top-right') {
      host.top = '20px';
      host.right = '20px';
      chatWindow.top = '90px';
      chatWindow.right = '20px';
    } else if (position === 'top-left') {
      host.top = '20px';
      host.left = '20px';
      chatWindow.top = '90px';
      chatWindow.left = '20px';
    } else {
      host.bottom = '20px';
      host.right = '20px';
      chatWindow.bottom = '90px';
      chatWindow.right = '20px';
    }
  }

  /**
   * Get position styles based on config
   */
  getPositionStyles() {
    const position = this.options.position;
    const baseStyles = 'position: fixed; z-index: 999999;';

    if (position === 'bottom-right') {
      return `${baseStyles} bottom: 20px; right: 20px;`;
    } else if (position === 'bottom-left') {
      return `${baseStyles} bottom: 20px; left: 20px;`;
    } else if (position === 'top-right') {
      return `${baseStyles} top: 20px; right: 20px;`;
    } else if (position === 'top-left') {
      return `${baseStyles} top: 20px; left: 20px;`;
    }

    return `${baseStyles} bottom: 20px; right: 20px;`;
  }

  /**
   * Get chat window position styles based on config
   */
  getChatWindowPositionStyles() {
    const position = this.options.position;
    // Don't include width/height - let CSS classes handle sizing
    const baseStyles = 'position: fixed; z-index: 999999;';

    if (position === 'bottom-right') {
      return `${baseStyles} bottom: 90px; right: 20px;`;
    } else if (position === 'bottom-left') {
      return `${baseStyles} bottom: 90px; left: 20px;`;
    } else if (position === 'top-right') {
      return `${baseStyles} top: 90px; right: 20px;`;
    } else if (position === 'top-left') {
      return `${baseStyles} top: 90px; left: 20px;`;
    }

    return `${baseStyles} bottom: 90px; right: 20px;`;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close button
    this.elements.closeBtn.onclick = () => this.toggleChat();

    // Expand button
    this.elements.expandBtn.onclick = () => this.toggleExpand();

    // New chat button
    this.elements.newChatBtn.onclick = () => this.startNewChat();

    // Send button
    this.elements.sendBtn.onclick = () => this.sendMessage();

    // Enter key in input
    this.elements.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !this.isLoading) {
        this.sendMessage();
      }
    });

    // Click outside to close (optional)
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.elements.host.contains(e.target)) {
        // Optional: close on outside click
        // this.toggleChat();
      }
    });
  }

  /**
   * Start a new conversation while keeping the same anonymous session.
   */
  restoreStoredTranscript() {
    if (this.messages.length) return;
    const stored = getStoredTranscript(this.storageScope);
    if (!stored.length) return;
    // Without a conversation id the server would start a fresh conversation
    // anyway; a stale transcript would be misleading. Drop it.
    if (!this.conversationId) {
      clearStoredTranscript(this.storageScope);
      return;
    }
    stored.forEach((entry) => {
      this.addMessage(entry.role, entry.content, null,
        entry.rich_content ? { rich_content: entry.rich_content } : null,
        { persist: false, timestamp: entry.ts || null });
    });
  }

  startNewChat() {
    if (this.isLoading) return;

    this.conversationId = null;
    this.messages = [];
    this.feedbackState = {};
    clearConversation(this.storageScope);
    clearStoredTranscript(this.storageScope);

    if (this.elements?.messagesContainer) {
      this.elements.messagesContainer.innerHTML = '';
    }
    this.ensureWelcomeMessage();
    if (this.elements?.input) {
      this.elements.input.focus();
    }
  }

  /**
   * Render the tenant-configured welcome message as the first assistant
   * bubble when the conversation is empty. Tenant-controlled content has the
   * same trust boundary as assistant replies (set by the same admin via the
   * Control Plane), so it is rendered through renderMarkdown like any other
   * assistant message.
   */
  ensureWelcomeMessage() {
    const text = this.styleConfig?.welcome_message;
    if (!text) return;
    const messages = this.elements?.messagesContainer;
    if (!messages) return;
    if (messages.querySelector('.ragsense-message.welcome')) return;

    const messageDiv = document.createElement('div');
    messageDiv.className = 'ragsense-message assistant welcome';
    const contentDiv = document.createElement('div');
    contentDiv.className = 'ragsense-message-content';
    // eslint-disable-next-line no-unsanitized/property -- renderMarkdown returns sanitized HTML
    contentDiv.innerHTML = this.renderMarkdown(text);
    messageDiv.appendChild(contentDiv);
    messages.appendChild(messageDiv);
  }

  /**
   * Toggle chat window
   */
  toggleChat() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.elements.chatWindow.hidden = false;
      this.elements.chatWindow.classList.remove('hidden');
      this.elements.button.style.display = 'none';
      this.elements.input.focus();
    } else {
      this.elements.chatWindow.hidden = true;
      this.elements.chatWindow.classList.add('hidden');
      this.elements.button.style.display = 'flex';
    }
  }

  /**
   * Send a message
   */
  async sendMessage() {
    const message = this.elements.input.value.trim();
    if (!message || this.isLoading) return;
    const conversationHistory = this.messages
      .filter(item => item.role === 'user' || item.role === 'assistant')
      .filter(item => item.content && String(item.content).trim())
      .slice(-10)
      .map(item => ({
        role: item.role,
        content: item.content
      }));

    // Clear input
    this.elements.input.value = '';

    // Add user message to UI
    this.addMessage('user', message);

    // Show loading
    this.setLoading(true);

    try {
      // Send to API
      let response;
      try {
        response = await this.apiClient.sendMessage(
          message,
          this.conversationId,
          this.browserFingerprint,
          this.utmParams,
          this.language,
          conversationHistory
        );
      } catch (error) {
        // Self-heal a stale conversation reference (purged server-side, or a
        // stored id from another environment): drop it and retry once on a
        // fresh thread instead of surfacing the raw error.
        const detail = String((error && error.message) || '');
        if (this.conversationId && /conversation not found|access denied/i.test(detail)) {
          console.warn('Stored conversation is unknown to the server; starting a new one.');
          this.conversationId = null;
          clearConversation(this.storageScope);
          response = await this.apiClient.sendMessage(
            message,
            null,
            this.browserFingerprint,
            this.utmParams,
            this.language,
            conversationHistory
          );
        } else {
          throw error;
        }
      }

      // Update conversation ID
      if (response.session_id && response.session_id !== this.sessionId) {
        this.sessionId = response.session_id;
        this.apiClient.sessionId = response.session_id;
        // The widget session token is bound to the old session ID; a fresh
        // one is minted on the next request.
        this.apiClient.clearSessionToken();
        setSessionId(response.session_id, this.storageScope);
        this.conversationId = null;
      }

      if (response.conversation_id) {
        this.conversationId = response.conversation_id;
        setConversationId(response.conversation_id, this.storageScope);
      }

      // Add assistant message to UI; addMessage positions the answer's start in view.
      this.addMessage('assistant', response.answer, response.message_id, response.metadata);
    } catch (error) {
      console.error('Failed to send message:', error);
      this.showError(error.message || this.t('sendError'));
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Add a message to the UI
   */
  addMessage(role, content, messageId = null, metadata = null, opts = {}) {
    const { persist = true, timestamp = null } = opts;
    this.messages.push({ role, content: String(content || '') });
    if (persist) {
      appendToStoredTranscript(this.storageScope, {
        role,
        content,
        rich_content: metadata?.rich_content,
      });
    }

    const messageDiv = document.createElement('div');
    messageDiv.className = `ragsense-message ${role}`;

    const contentDiv = document.createElement('div');
    contentDiv.className = 'ragsense-message-content';

    // Render markdown for assistant messages, plain text for user
    if (role === 'assistant') {
      contentDiv.innerHTML = this.renderMarkdown(content);
      this.renderRichContent(contentDiv, metadata?.rich_content);

      // Auto-expand if content contains a table
      if (contentDiv.querySelector('table') && !this.elements.chatWindow.classList.contains('expanded')) {
        setTimeout(() => this.toggleExpand(), 300);
      }
    } else {
      // Use textContent for user messages (safe, no HTML)
      contentDiv.textContent = content;
    }

    const timeDiv = document.createElement('div');
    timeDiv.className = 'ragsense-message-time';
    timeDiv.textContent = this.formatTime(timestamp ? new Date(timestamp) : new Date());

    messageDiv.appendChild(contentDiv);

    // Add emoji reaction button for assistant messages
    if (role === 'assistant' && messageId) {
      const reactionBtn = this.createReactionButton(messageId);
      contentDiv.appendChild(reactionBtn);
    }

    messageDiv.appendChild(timeDiv);

    this.elements.messagesContainer.appendChild(messageDiv);
    if (role === 'assistant' && persist) {
      // Keep the start of a new answer in view; the visitor reads top-down.
      scrollToMessageStart(this.elements.messagesContainer, messageDiv);
    } else {
      this.scrollToBottom();
    }
  }

  renderRichContent(container, blocks) {
    if (!Array.isArray(blocks) || blocks.length === 0) return;

    blocks.forEach(block => {
      if (!block || typeof block !== 'object') return;
      if (block.type === 'show_gallery') {
        this.renderShowGallery(container, block.items);
      } else if (block.type === 'resource_list') {
        this.renderResourceList(container, block.items);
      }
    });
  }

  renderResourceList(container, items) {
    if (!Array.isArray(items) || items.length === 0) return;

    const safeItems = items
      .filter(item => item && typeof item === 'object' && this.safeUrl(item.url))
      .slice(0, 5);
    if (!safeItems.length) return;

    // Collapsed by default — resources are supporting material, not the
    // answer; a native <details> keeps it accessible with zero extra JS.
    const section = document.createElement('details');
    section.className = 'ragsense-resources';

    const heading = document.createElement('summary');
    heading.className = 'ragsense-resources-title';
    heading.textContent = `${this.t('resources')} (${safeItems.length})`;
    section.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'ragsense-resources-list';

    safeItems.forEach(itemData => {
      const url = this.safeUrl(itemData.url);
      if (!url) return;

      const link = document.createElement('a');
      link.className = 'ragsense-resource-link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      const title = document.createElement('span');
      title.className = 'ragsense-resource-title';
      title.textContent = itemData.title || url;
      link.appendChild(title);

      if (itemData.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'ragsense-resource-subtitle';
        subtitle.textContent = String(itemData.subtitle);
        link.appendChild(subtitle);
      }

      list.appendChild(link);
    });

    if (list.childElementCount) {
      section.appendChild(list);
      container.appendChild(section);
    }
  }

  renderShowGallery(container, items) {
    if (!Array.isArray(items) || items.length === 0) return;

    const list = document.createElement('div');
    list.className = 'ragsense-show-cards';

    items.filter(item => item && typeof item === 'object').slice(0, 6).forEach(itemData => {
      const item = document.createElement('article');
      item.className = 'ragsense-show-card';

      const imageUrl = this.safeUrl(itemData.image_url);
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'ragsense-show-card-image';
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        item.appendChild(image);
      }

      const body = document.createElement('div');
      body.className = 'ragsense-show-card-body';

      const title = document.createElement('div');
      title.className = 'ragsense-show-card-title';
      title.textContent = itemData.title || this.t('itemFallback');
      body.appendChild(title);

      if (itemData.subtitle) {
        const subtitle = document.createElement('div');
        subtitle.className = 'ragsense-show-card-meta';
        subtitle.textContent = itemData.subtitle;
        body.appendChild(subtitle);
      }

      const scheduleParts = [itemData.date, itemData.time].filter(Boolean);
      if (scheduleParts.length || itemData.duration) {
        const schedule = document.createElement('div');
        schedule.className = 'ragsense-show-card-meta';
        schedule.textContent = [scheduleParts.join(' '), itemData.duration].filter(Boolean).join(' · ');
        body.appendChild(schedule);
      }

      if (Array.isArray(itemData.facts)) {
        itemData.facts.filter(Boolean).slice(0, 4).forEach(fact => {
          const factEl = document.createElement('div');
          factEl.className = 'ragsense-show-card-meta';
          factEl.textContent = String(fact);
          body.appendChild(factEl);
        });
      }

      const actions = document.createElement('div');
      actions.className = 'ragsense-show-card-actions';
      const primaryUrl = this.safeUrl(itemData.primary_url);
      const secondaryUrl = this.safeUrl(itemData.secondary_url);
      if (primaryUrl) {
        actions.appendChild(this.createCardLink(primaryUrl, itemData.primary_label || 'Open'));
      }
      if (secondaryUrl) {
        actions.appendChild(this.createCardLink(secondaryUrl, itemData.secondary_label || 'More'));
      }
      if (actions.childElementCount) {
        body.appendChild(actions);
      }

      item.appendChild(body);
      list.appendChild(item);
    });

    if (list.childElementCount) {
      container.appendChild(list);
    }
  }

  createCardLink(url, label) {
    const link = document.createElement('a');
    link.className = 'ragsense-show-card-link';
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = label;
    return link;
  }

  safeUrl(value) {
    if (!value || typeof value !== 'string') return null;
    try {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      return url.toString();
    } catch (_error) {
      return null;
    }
  }

  /**
   * Create reaction button with emoji picker
   */
  createReactionButton(messageId) {
    const container = document.createElement('div');
    container.className = 'ragsense-reaction-container';

    // Main reaction button (empty emoji - outline style)
    const btn = document.createElement('button');
    btn.className = 'ragsense-reaction-btn';
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
      <line x1="9" y1="9" x2="9.01" y2="9"></line>
      <line x1="15" y1="9" x2="15.01" y2="9"></line>
    </svg>`;
    btn.title = 'React to this message';
    btn.setAttribute('aria-label', 'React to this message');

    // Emoji picker
    const picker = document.createElement('div');
    picker.className = 'ragsense-emoji-picker hidden';

    const emojis = [
      { emoji: '👍', type: 'thumbs_up', label: 'Like' },
      { emoji: '👎', type: 'thumbs_down', label: 'Dislike' }
    ];

    emojis.forEach(({ emoji, type, label }) => {
      const emojiBtn = document.createElement('button');
      emojiBtn.className = 'ragsense-emoji-option';
      emojiBtn.innerHTML = emoji;
      emojiBtn.title = label;
      emojiBtn.onclick = async (e) => {
        e.stopPropagation();
        await this.submitFeedback(messageId, type, emoji, btn);
        picker.classList.add('hidden');
      };
      picker.appendChild(emojiBtn);
    });

    // Toggle picker
    btn.onclick = (e) => {
      e.stopPropagation();

      // Close other pickers — scoped to our shadow root, since pickers live
      // inside the widget's shadow tree and are not reachable from document.
      const root = this.shadowRoot || document;
      root.querySelectorAll('.ragsense-emoji-picker').forEach(p => {
        if (p !== picker) p.classList.add('hidden');
      });

      picker.classList.toggle('hidden');
    };

    // Close picker when clicking outside. Event targets are retargeted to the
    // shadow host for document-level listeners, so we use composedPath() to
    // see whether the click actually originated inside this reaction container.
    document.addEventListener('click', (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (!path.includes(container)) {
        picker.classList.add('hidden');
      }
    });

    container.appendChild(btn);
    container.appendChild(picker);

    return container;
  }

  /**
   * Submit feedback/reaction
   */
  async submitFeedback(messageId, feedbackType, emoji, buttonElement) {
    try {
      // Check if already submitted
      if (this.feedbackState[messageId]) {
        return;
      }

      await this.apiClient.submitFeedback(messageId, feedbackType);

      // Update button to show selected emoji
      buttonElement.innerHTML = emoji;
      buttonElement.classList.add('active');
      buttonElement.disabled = true;
      buttonElement.style.cursor = 'default';

      // Track feedback state
      this.feedbackState[messageId] = feedbackType;
    } catch (error) {
      console.error('Failed to submit reaction:', error);
    }
  }

  /**
   * Show loading state
   */
  setLoading(loading) {
    this.isLoading = loading;

    if (loading) {
      // Add loading indicator
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'ragsense-message assistant';
      loadingDiv.innerHTML = `
        <div class="ragsense-loading">
          <div class="ragsense-loading-dot"></div>
          <div class="ragsense-loading-dot"></div>
          <div class="ragsense-loading-dot"></div>
        </div>
      `;
      this.elements.messagesContainer.appendChild(loadingDiv);

      // Disable input
      this.elements.input.disabled = true;
      this.elements.sendBtn.disabled = true;
      this.elements.newChatBtn.disabled = true;

      this.scrollToBottom();
    } else {
      // Remove loading indicator
      const loadingDiv = this.elements.messagesContainer.querySelector('.ragsense-loading');
      if (loadingDiv) {
        loadingDiv.parentElement.remove();
      }

      // Enable input
      this.elements.input.disabled = false;
      this.elements.sendBtn.disabled = false;
      this.elements.newChatBtn.disabled = false;
      this.elements.input.focus();
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (!this.elements?.messagesContainer) {
      console.error('Unable to render widget error:', message);
      return;
    }

    if (!this.isOpen) {
      this.toggleChat();
    }

    const errorDiv = document.createElement('div');
    errorDiv.className = 'ragsense-error';
    errorDiv.textContent = message;
    this.elements.messagesContainer.appendChild(errorDiv);

    // Remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);

    this.scrollToBottom();
  }

  /**
   * Scroll to bottom of messages
   */
  scrollToBottom() {
    const container = this.elements.messagesContainer;
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Format time
   */
  formatTime(date) {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Render markdown to HTML using marked library
   */
  renderMarkdown(text) {
    // Configure marked for security and features
    marked.setOptions({
      breaks: true,        // Convert \n to <br>
      gfm: true,          // GitHub Flavored Markdown (tables, etc.)
      headerIds: false,    // Don't add IDs to headers
      mangle: false,       // Don't escape email addresses
      sanitize: false,     // DOMPurify sanitizes the generated HTML below.
    });

    // Parse markdown to HTML
    const html = marked.parse(text);

    return DOMPurify.sanitize(html, MARKDOWN_SANITIZE_CONFIG);
  }

  /**
   * Get neutral fallback logo as base64 encoded SVG.
   */
  getLogoBase64() {
    const svg = `<?xml version="1.0" encoding="utf-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="12" fill="#202020"/><path d="M18 42V20h15c5 0 8 3 8 7 0 3-2 5-4 6l6 9h-8l-5-8h-5v8h-7zm7-14h7c1 0 2-1 2-2s-1-2-2-2h-7v4z" fill="#fff"/><path d="M45 22h4v20h-4z" fill="#D50032"/></svg>`;
    return btoa(svg);
  }

  /**
   * Toggle expanded state
   */
  toggleExpand() {
    const isExpanded = this.elements.chatWindow.classList.toggle('expanded');

    // Update icon
    if (isExpanded) {
      this.elements.expandBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"></path>
      </svg>`;
      this.elements.expandBtn.title = 'Minimize';
    } else {
      this.elements.expandBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l-7"></path>
      </svg>`;
      this.elements.expandBtn.title = 'Expand';
    }

    // Save state
    localStorage.setItem('ragsense_chat_expanded', isExpanded);

    // Scroll to bottom after resize
    setTimeout(() => this.scrollToBottom(), 100);
  }

  /**
   * Load saved expanded state
   */
  loadExpandedState() {
    const isExpanded = localStorage.getItem('ragsense_chat_expanded') === 'true';
    if (isExpanded) {
      this.elements.chatWindow.classList.add('expanded');
      this.elements.expandBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"></path>
      </svg>`;
      this.elements.expandBtn.title = 'Minimize';
    }
  }
}

// Export as global singleton
const widget = new RagSenseWidget();

function autoInitFromScript() {
  const currentScript = document.currentScript;
  const currentScriptMatches =
    currentScript instanceof HTMLScriptElement &&
    currentScript.matches('script[data-ragsense-config], script[data-ragsense-token]') &&
    !currentScript.dataset.ragsenseInitialized;
  const script = currentScriptMatches
    ? currentScript
    : document.querySelector('script[data-ragsense-config]:not([data-ragsense-initialized]), script[data-ragsense-token]:not([data-ragsense-initialized])');
  if (!script) return;

  try {
    script.dataset.ragsenseInitialized = 'true';
    // CSS is no longer injected globally — the widget now creates its own
    // shadow root and loads ragsense-widget.css inside it (see createUI).
    if (script.src) {
      widgetStylesheetUrl = new URL('ragsense-widget.css', script.src).toString();
    }
    if (script.dataset.ragsenseConfig) {
      const config = JSON.parse(script.dataset.ragsenseConfig);
      refineStylesheetUrlForApi(config.apiBaseUrl);
      widget.init(config);
      return;
    }
    if (script.dataset.ragsenseToken) {
      const apiBaseUrl = script.dataset.ragsenseApi || window.location.origin;
      refineStylesheetUrlForApi(apiBaseUrl);
      widget.init({
        apiBaseUrl,
        configToken: script.dataset.ragsenseToken,
        telemetryEnabled: script.dataset.ragsenseTelemetry === 'true',
      });
    }
  } catch (e) {
    console.error('Failed to parse RAGSense widget config:', e);
  }
}

// Page optimizers (WP Rocket, Autoptimize, …) may minify and re-host this
// bundle on the customer's own domain, where no ragsense-widget.css exists
// next to it — the relative resolution above would then 404 and the widget
// renders unstyled. Whenever the stylesheet would load from a different
// origin than the RagSense API (or no script src was detectable at all),
// load it from the API host instead, which always serves it under /widget/.
function refineStylesheetUrlForApi(apiBaseUrl) {
  if (!apiBaseUrl) return;
  try {
    const apiOrigin = new URL(apiBaseUrl, window.location.href).origin;
    if (!widgetStylesheetUrl || new URL(widgetStylesheetUrl).origin !== apiOrigin) {
      widgetStylesheetUrl = `${apiOrigin}/widget/ragsense-widget.css`;
    }
  } catch (e) {
    // Keep whatever URL we already had — worst case is the pre-existing behavior.
  }
}

// Auto-initialize for one-line script embeds, including scripts added after DOMContentLoaded.
if (document.readyState === 'loading') {
  autoInitFromScript();
  document.addEventListener('DOMContentLoaded', autoInitFromScript, { once: true });
} else {
  autoInitFromScript();
}

// Export for manual initialization
export default {
  init: (options) => widget.init(options)
};

// Make available globally
if (typeof window !== 'undefined') {
  window.RagSenseWidget = {
    init: (options) => widget.init(options)
  };
}
